import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useHouseContext } from '@/context/HouseContext.tsx';
import { useHouseRecordStatus, HouseDocumentAggregates } from '@/hooks/house/documents/useHouseRecordStatus.ts';
import type { DocumentStatusV2Schema, PipelineStatusSchema } from '@/client/types.gen.ts';
import { useQueryClient } from '@tanstack/react-query';
import { refreshDocumentList } from '@/hooks/house/documents/documentListRefresh.ts';
import { houseDocumentKeys } from '@/hooks/house/documents/queries/queryKeys';
import { makeLogger } from '@/utils/logger/logger.ts';

const logger = makeLogger('RecordStatusContext');

type RecordStatusValue = {
	observation: RecordStatusObservation;
	polledStatusById: Record<number, DocumentStatusV2Schema>;
	aggregates: HouseDocumentAggregates;
	getPolledStatus: (id?: number | null) => DocumentStatusV2Schema | undefined;
	isProcessing: (id?: number | null) => boolean;
	isReady: (id?: number | null) => boolean;
	isFailed: (id?: number | null) => boolean;
};

type DocumentStatusStore = {
	observation: RecordStatusObservation;
	getSnapshot: (id?: number | null) => DocumentStatusV2Schema | undefined;
	subscribe: (id: number | null | undefined, onChange: () => void) => () => void;
};

export const RecordStatusObservation = {
	Unobserved: 'unobserved',
	Observed: 'observed',
	Unavailable: 'unavailable',
} as const;

export type RecordStatusObservation = (typeof RecordStatusObservation)[keyof typeof RecordStatusObservation];

const RecordStatusContext = createContext<RecordStatusValue | null>(null);
const DocumentStatusStoreContext = createContext<DocumentStatusStore | null>(null);

const NOOP_UNSUBSCRIBE = () => {};

function runSignature(run?: PipelineStatusSchema | null) {
	return [
		run?.status ?? '',
		run?.progress ?? '',
		run?.error ?? '',
		run?.started_at ?? '',
		run?.completed_at ?? '',
		run?.retry_count ?? '',
	].join(':');
}

function documentStatusSignature(status?: DocumentStatusV2Schema) {
	if (!status) {
		return '';
	}

	return [
		status.uploaded_at ?? '',
		runSignature(status.bnd_v2),
		runSignature(status.metadata_profiling),
		runSignature(status.metadata_extraction),
		runSignature(status.kbs),
		runSignature(status.document_enrichment),
	].join('|');
}

export function RecordStatusProvider({ children }: { children: React.ReactNode }) {
	const { selectedHouse } = useHouseContext();
	const houseId = selectedHouse?.id ?? null;
	const queryClient = useQueryClient();
	const wasEnriching = useRef(false);
	const wasUpdatingKB = useRef(false);
	const previousHouseId = useRef<number | null>(houseId);
	const statusHouseIdRef = useRef<number | null>(houseId);
	const statusByIdRef = useRef<Record<number, DocumentStatusV2Schema>>({});
	const statusSignaturesRef = useRef<Record<number, string>>({});
	const documentSubscribersRef = useRef<Map<number, Set<() => void>>>(new Map());
	const {
		statusById: polledStatusById,
		aggregates,
		getStatusById: getPolledStatus,
		isProcessing,
		isReady,
		isFailed,
		dataUpdatedAt,
		isError: isStatusError,
	} = useHouseRecordStatus(houseId);
	const observation =
		dataUpdatedAt > 0
			? RecordStatusObservation.Observed
			: isStatusError
				? RecordStatusObservation.Unavailable
				: RecordStatusObservation.Unobserved;
	statusByIdRef.current = polledStatusById;

	const documentStatusStore = useMemo<DocumentStatusStore>(
		() => ({
			observation,
			getSnapshot: (id) => (id != null ? statusByIdRef.current[id] : undefined),
			subscribe: (id, onChange) => {
				if (id == null) {
					return NOOP_UNSUBSCRIBE;
				}

				const existing = documentSubscribersRef.current.get(id);
				const subscribers = existing ?? new Set<() => void>();
				subscribers.add(onChange);
				if (!existing) {
					documentSubscribersRef.current.set(id, subscribers);
				}

				return () => {
					subscribers.delete(onChange);
					if (subscribers.size === 0) {
						documentSubscribersRef.current.delete(id);
					}
				};
			},
		}),
		[observation],
	);

	useEffect(() => {
		const houseChanged = statusHouseIdRef.current !== houseId;
		const previousSignatures = houseChanged ? {} : statusSignaturesRef.current;
		const nextSignatures: Record<number, string> = {};
		const changedDocumentIds = new Set<number>();
		const newlyObservedDocumentIds = new Set<number>();
		statusHouseIdRef.current = houseId;

		for (const id in polledStatusById) {
			const numericId = Number(id);
			const signature = documentStatusSignature(polledStatusById[numericId]);
			nextSignatures[numericId] = signature;
			if (!(numericId in previousSignatures)) {
				newlyObservedDocumentIds.add(numericId);
			}
			if (previousSignatures[numericId] !== signature) {
				changedDocumentIds.add(numericId);
			}
		}

		for (const id in previousSignatures) {
			const numericId = Number(id);
			if (!(numericId in nextSignatures)) {
				changedDocumentIds.add(numericId);
			}
		}

		statusByIdRef.current = polledStatusById;
		statusSignaturesRef.current = nextSignatures;

		changedDocumentIds.forEach((id) => {
			documentSubscribersRef.current.get(id)?.forEach((onChange) => onChange());
		});

		// Uploads created outside this browser (notably inbound email attachments) never run a local
		// mutation that can invalidate the documents query. Use the five-second status stream as the
		// fast path for new IDs; the slower Records-page poll covers visibility, edits, and removals.
		if (houseId && newlyObservedDocumentIds.size > 0) {
			const documentsQueryKey = houseDocumentKeys.list(houseId);
			const cachedDocuments = queryClient.getQueryData<Array<{ id: number }>>(documentsQueryKey);
			if (cachedDocuments) {
				const cachedDocumentIds = new Set(cachedDocuments.map((document) => document.id));
				const hasUnlistedDocument = [...newlyObservedDocumentIds].some((id) => !cachedDocumentIds.has(id));
				if (hasUnlistedDocument) {
					logger.info('document status discovered an unlisted document - refreshing documents list');
					// Through the shared refresher: while a drop is landing this fires on every poll,
					// and it would otherwise spend a budget the uploads themselves are also drawing on.
					refreshDocumentList(queryClient, houseId);
				}
			}
		}
	}, [houseId, polledStatusById, queryClient]);

	useEffect(() => {
		if (previousHouseId.current === houseId) {
			return;
		}

		previousHouseId.current = houseId;
		wasEnriching.current = aggregates.isEnrichingDocuments;
		wasUpdatingKB.current = aggregates.isUpdatingKnowledgeBase;
	}, [aggregates.isEnrichingDocuments, aggregates.isUpdatingKnowledgeBase, houseId]);

	useEffect(() => {
		const isEnriching = aggregates.isEnrichingDocuments;

		if (wasEnriching.current && !isEnriching && houseId) {
			logger.info('document_enrichment transitioned to completed - invalidating documents list');
			queryClient.invalidateQueries({ queryKey: houseDocumentKeys.list(houseId) });
		} else {
			logger.debug(
				`document_enrichment transition - isEnriching: ${isEnriching}, wasEnriching: ${wasEnriching.current}`,
			);
		}

		wasEnriching.current = isEnriching;
	}, [aggregates.isEnrichingDocuments, houseId, queryClient]);

	useEffect(() => {
		const isUpdatingKB = aggregates.isUpdatingKnowledgeBase;

		if (wasUpdatingKB.current && !isUpdatingKB && houseId) {
			logger.info('KB pipeline transitioned to completed - invalidating documents list');
			queryClient.invalidateQueries({ queryKey: houseDocumentKeys.list(houseId) });
		} else {
			logger.debug(
				`KB pipeline transition - isUpdatingKB: ${isUpdatingKB}, wasUpdatingKB: ${wasUpdatingKB.current}`,
			);
		}

		wasUpdatingKB.current = isUpdatingKB;
	}, [aggregates.isUpdatingKnowledgeBase, houseId, queryClient]);

	const value = useMemo<RecordStatusValue>(
		() => ({ observation, polledStatusById, aggregates, getPolledStatus, isProcessing, isReady, isFailed }),
		[observation, polledStatusById, aggregates, getPolledStatus, isProcessing, isReady, isFailed],
	);

	return (
		<DocumentStatusStoreContext.Provider value={documentStatusStore}>
			<RecordStatusContext.Provider value={value}>{children}</RecordStatusContext.Provider>
		</DocumentStatusStoreContext.Provider>
	);
}

export function useRecordStatusContext() {
	const ctx = useContext(RecordStatusContext);
	if (!ctx) {
		throw new Error('useRecordStatusContext must be used within a RecordStatusProvider');
	}
	return ctx;
}

export function useDocumentStatusStore() {
	const ctx = useContext(DocumentStatusStoreContext);
	if (!ctx) {
		throw new Error('useDocumentStatusStore must be used within a RecordStatusProvider');
	}
	return ctx;
}
